// utils/transformStudyPlan.js

import { resolveSiteIdEffort } from "./siteIdEffort";

const months = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function convertMillisToDateString(millis) {
  const date = new Date(millis);
  const day = String(date.getDate()).padStart(2, "0");
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function findMilestone(milestones, key, isDescr = false) {
  return milestones.find((m) =>
    isDescr ? m.milestoneDescr === key : m.milestoneName === key
  );
}

export function buildTransformedObject(changelog, studyId) {
  const countries = changelog.countries;

  return {
    studyId,
    studyPlanId: changelog.studyPlanId,
    studyPlanType: changelog.studyPlanTypeDetail,
    tables: [
      {
        tableName: "Country Inputs",
        countries: countries.map((country) => {
          const sg = country.siteGroups[0];
          const lowerBound = sg.parameters[0].lowerBound;

          return {
            countryName: country.countryName,
            countryStartUpTimeFrame: sg.timeCountryStartUpSize,
            enrollmentRate: sg.siteEnrollmentRateAtSIV,
            NUM_SITES_ACTIVATED_LOWER: sg.parameters[0].lowerBound,
            siteIdEffort:
              resolveSiteIdEffort(
                sg.timeSiteIdSize,
                lowerBound,
                sg.parameters[0].upperBound,
              ) || "",
            NUM_SITES_ACTIVATED_UPPER: sg.parameters[0].upperBound,
            NUM_SUBJECTS_COMPLETED:
              findMilestone(sg.milestones, "NUM_SUBJECTS_COMPLETED")?.lowerBound ?? null,
            NUM_SUBJECTS_ENROLLED:
              findMilestone(sg.milestones, "NUM_SUBJECTS_ENROLLED")?.lowerBound ?? null,
            NUM_SUBJECTS_SCREENED_LOWER: sg.parameters[1].lowerBound,
            NUM_SUBJECTS_SCREENED_UPPER: sg.parameters[1].upperBound,
            NUM_SUBJECTS_TREATED:
              findMilestone(sg.milestones, "NUM_SUBJECTS_TREATED")?.lowerBound ?? null,
            dropOutRate: sg.expectedTreatmentDropoutRate,
            screenFailRate: sg.expectedScreenFailureRate,
          };
        }),
      },
      {
        tableName: "Milestones",
        countries: countries.map((country) => {
          const sg = country.siteGroups[0];
          const get = (name) => findMilestone(sg.milestones, name);
          const dateOrEmpty = (m) =>
            m ? convertMillisToDateString(m.milestoneDateMillis) : "";
          const commentOrEmpty = (m) => (m ? m.comments : "");

          const SITE_ID = get("DURATION_TO_SITE_ID_FINISH");
          const SUBMISSION = get("DURATION_TO_SUBMISSION");
          const FIRST_REGULATORY = get("DURATION_TO_FIRST_REGULATORY_APPROVAL");
          const FSIV = get("DURATION_TO_FSIV");
          const FPS = get("DURATION_TO_FPS");
          const FPI = get("DURATION_TO_FPI");
          const LPI = get("DURATION_TO_LPI");

          return {
            countryName: country.countryName,
            SITE_ID_DATE: dateOrEmpty(SITE_ID),
            SITE_ID_COMMENT: commentOrEmpty(SITE_ID),
            SUBMISSION_DATE: dateOrEmpty(SUBMISSION),
            SUBMISSION_COMMENT: commentOrEmpty(SUBMISSION),
            FIRST_REGULATORY_APPROVAL_DATE: dateOrEmpty(FIRST_REGULATORY),
            FIRST_REGULATORY_APPROVAL_COMMENT: commentOrEmpty(FIRST_REGULATORY),
            FSIV_DATE: dateOrEmpty(FSIV),
            FSIV_COMMENT: commentOrEmpty(FSIV),
            FPS_DATE: dateOrEmpty(FPS),
            FPS_COMMENT: commentOrEmpty(FPS),
            FPI_DATE: dateOrEmpty(FPI),
            FPI_COMMENT: commentOrEmpty(FPI),
            LPI_DATE: dateOrEmpty(LPI),
            LPI_COMMENT: commentOrEmpty(LPI),
          };
        }),
      },
      {
        tableName: "Activation Profiles",
        countries: countries.flatMap((country) => {
          const profiles = country.siteGroups[0].activationProfiles;
          const profileObject = {
            countryName: country.countryName,
            overrideFl: profiles?.[0]?.overrideFl,
          };

          profiles.forEach((profile, i) => {
            profileObject[`CARD_${i + 1}_PERCENT`] = parseFloat(
              profile.siteActivationProfilePct ?? undefined
            );
            profileObject[`CARD_${i + 1}_DAYS`] = profile.siteActivationProfilePct
              ? profile.siteActivationProfileDays
              : undefined;
          });

          return [profileObject];
        }),
      },
      {
        tableName: "Enrollment Seasonal Variations",
        countries: countries.flatMap((country) => {
          const monthsObject = { countryName: country.countryName };
          country.siteGroups[0].enrollmentVariations.forEach((month) => {
            monthsObject[`MONTH_${month.enrollmentSeasonalMonth + 1}`] =
              parseFloat(month.enrollmentSeasonalVariation);
          });
          return [monthsObject];
        }),
      },
      {
        tableName: "Site Fatigue",
        countries: countries.map((country) => ({
          countryName: country.countryName,
          SITE_FATIGUE_DAYS: country.siteGroups[0].eventualSiteEnrollmentDays || undefined,
          SITE_FATIGUE_PERCENT: country.siteGroups[0].eventualSiteEnrollmentRate || undefined,
        })),
      },
    ],
  };
}